import { Recapt } from '../../engine';
import {
  FeedbackSubmitPayload,
  FeedbackWidgetPayload,
  UserCommentPositioning,
} from '../../types';
import { BaseEvent, BaseModule } from '../base';
import { Namespace } from '../types';
import { VALID_WIDGET_ACTIONS } from './constants';
import { SetupUserCommentsScript, UserCommentDOMValue } from './content';

interface CommentEvent extends BaseEvent {
  comment: string;
  rating?: number;
}

export class UserCommentsModule extends BaseModule<CommentEvent> {
  public namespace: Namespace = 'userComments';
  private hasIdentity = false;
  private posInfoStyling: string = 'bottom: 12px; right: 12px;';
  private isWidgetVisible = false;

  /**
   * Submit feedback programmatically (for custom UIs).
   * Validates input defensively - invalid calls are silently ignored.
   */
  submitFeedback(message: string, rating?: number) {
    if (typeof message !== 'string' || message.trim().length === 0) {
      Recapt.debug('feedback: message must be a non-empty string');
      return;
    }

    const trimmedMessage = message.trim();
    if (trimmedMessage.length > 5000) {
      Recapt.debug('feedback: message exceeds 5000 character limit');
      return;
    }

    const event: CommentEvent = {
      comment: trimmedMessage,
      timestamp: Recapt.getCurrentSessionTimestamp(),
      where: Recapt.where(),
    };

    if (typeof rating === 'number') {
      const clampedRating = Math.max(1, Math.min(5, Math.round(rating)));
      event.rating = clampedRating;
    }

    this.state.push(event);
    Recapt.debug('feedback: submitted', event.comment.slice(0, 50) + '...');
  }

  onComment(comment: string) {
    this.state.push({
      comment,
      timestamp: Recapt.getCurrentSessionTimestamp(),
      where: Recapt.where(),
    });
  }

  public setPos(pos: UserCommentPositioning) {
    switch (pos) {
      case 'top-left':
        this.posInfoStyling = 'top: 12px; left: 12px;';
        break;
      case 'top-right':
        this.posInfoStyling = 'top: 12px; right: 12px;';
        break;
      case 'bottom-left':
        this.posInfoStyling = 'bottom: 12px; left: 12px;';
        break;
      case 'bottom-right':
        this.posInfoStyling = 'bottom: 12px; right: 12px;';
        break;
      case 'bottom-center':
        this.posInfoStyling =
          'bottom: 12px; left: 50%; transform: translateX(-50%);';
        break;
      case 'top-center':
        this.posInfoStyling =
          'top: 12px; left: 50%; transform: translateX(-50%);';
        break;
      default:
        this.posInfoStyling = 'bottom: 12px; right: 12px;';
        break;
    }
  }

  /**
   * Show the feedback widget. Can be called dynamically after start().
   */
  showWidget(position?: UserCommentPositioning) {
    if (position) {
      this.setPos(position);
    }
    this.isWidgetVisible = true;
    this.draw();
    Recapt.debug('feedback: widget shown');
  }

  /**
   * Hide the feedback widget.
   */
  hideWidget() {
    this.isWidgetVisible = false;
    this.destroy();
    Recapt.debug('feedback: widget hidden');
  }

  /**
   * Open the feedback form (expand the widget).
   */
  openForm() {
    const element = document.getElementById(
      '_recapt-user-comments-container-parent',
    );
    if (!element?.shadowRoot) {
      Recapt.debug('feedback: widget not found, showing first');
      this.showWidget();
      return;
    }

    const content = element.shadowRoot.getElementById('_recapt-content');
    if (content) {
      content.classList.add('_recapt-open');
      Recapt.debug('feedback: form opened');
    }
  }

  /**
   * Close the feedback form (collapse the widget).
   */
  closeForm() {
    const element = document.getElementById(
      '_recapt-user-comments-container-parent',
    );
    if (!element?.shadowRoot) return;

    const content = element.shadowRoot.getElementById('_recapt-content');
    if (content) {
      content.classList.remove('_recapt-open');
      Recapt.debug('feedback: form closed');
    }
  }

  /**
   * Handle widget control actions (show, hide, open, close).
   * Called from Recapt.feedback() for widget payloads.
   */
  handleWidget(payload: FeedbackWidgetPayload) {
    if (!VALID_WIDGET_ACTIONS.includes(payload.widget)) {
      Recapt.debug('feedback() invalid widget action:', payload.widget);
      return;
    }

    switch (payload.widget) {
      case 'show':
        this.showWidget(payload.position);
        break;
      case 'hide':
        this.hideWidget();
        break;
      case 'open':
        this.openForm();
        break;
      case 'close':
        this.closeForm();
        break;
    }
  }

  /**
   * Handle feedback submission.
   * Called from Recapt.feedback() for submit payloads.
   */
  handleSubmit(payload: FeedbackSubmitPayload) {
    this.submitFeedback(payload.message, payload.rating);
  }

  draw() {
    Recapt.debug('drawing user comments');

    const existingElement = document.getElementById(
      '_recapt-user-comments-container-parent',
    );

    if (existingElement && existingElement.isConnected) return;

    if (Recapt.userCommentsInfo.positioning) {
      this.setPos(Recapt.userCommentsInfo.positioning);
    }

    const sessionId = Recapt.sessionId;
    if (!sessionId) {
      Recapt.debug('no session id found, skipping user comments');
      return;
    }

    const sessionUrl = `https://dash.recapt.app/sessions/${sessionId}`;

    const element = document.createElement('div');
    element.setAttribute('id', '_recapt-user-comments-container-parent');
    document.body.appendChild(element);

    const shadow = element.attachShadow({ mode: 'open' });

    shadow.innerHTML = UserCommentDOMValue(sessionId, this.posInfoStyling);

    SetupUserCommentsScript(shadow, sessionUrl);
  }

  destroy() {
    const element = document.getElementById(
      '_recapt-user-comments-container-parent',
    );
    if (!element) return;
    element.remove();
  }

  onIdentity() {
    // if (this.hasIdentity) return;
    // //we have a session identity.

    // this.hasIdentity = true;
    this.draw();
  }

  onNewSession() {
    super.onNewSession();
    this.draw();
  }

  onStart() {
    //@ts-expect-error
    window.addEventListener('_recapt-comment', eventListener);

    this.draw();
  }

  onStop() {
    //we can run destroy even if we don't have a user
    this.destroy();
    //@ts-expect-error
    window.removeEventListener('_recapt-comment', eventListener);
  }

  purgeState(): void {
    this.state = [];
  }

  buildRequest(
    state: CommentEvent[],
  ): Record<string, unknown> & { state: CommentEvent[] } {
    return {
      state,
    };
  }
}

const eventListener = (e: CustomEvent<{ comment: string }>) => {
  Recapt.getModule<UserCommentsModule>('userComments')?.onComment(
    e.detail.comment,
  );
};
